import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ADMIN_BASE } from "@admin/constants/route";

/**
 * Admin login screen. Reuses the user app's `LoginModal` (Google + phone OTP)
 * by triggering `login()` from AuthContext. Once authenticated as an admin, we
 * redirect to the originally-requested admin page (or ADMIN_BASE).
 *
 * Non-admins who somehow find this URL and sign in get bounced to "/" — they
 * never see the admin shell.
 */
export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || ADMIN_BASE;

  useEffect(() => {
    // Inject noindex on the login page too so the entry point isn't crawlable.
    let meta = document.querySelector('meta[name="robots"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", "noindex,nofollow,noarchive,nosnippet");
    return () => {
      if (created) meta.parentNode && meta.parentNode.removeChild(meta);
      else if (prev != null) meta.setAttribute("content", prev);
    };
  }, []);

  useEffect(() => {
    if (!loading && user) {
      if (user.role === "admin") navigate(from, { replace: true });
      else navigate("/", { replace: true });
    }
  }, [user, loading, navigate, from]);

  return (
    <div className="min-h-screen bg-[#0A2540] grid place-items-center p-6">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl" data-testid="admin-login-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-[#D4AF37] grid place-items-center text-[#0A2540] font-extrabold text-xl">A</div>
          <div>
            <p className="text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Advoset Admin</p>
            <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Legal Help, Simplified</p>
          </div>
        </div>

        <h1 className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
          Sign in to continue
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Use your authorised admin account. Permissions are role-based — your sidebar adapts to what you can do.
        </p>

        <Button
          onClick={login}
          data-testid="admin-login-btn"
          className="mt-6 w-full rounded-xl adv-btn-gold font-bold h-11"
        >
          <ShieldCheck size={16} /> Sign in
        </Button>

        <div className="mt-5 flex items-center justify-center gap-4 text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1"><Lock size={12} /> RBAC protected</span>
          <span className="inline-flex items-center gap-1"><ShieldCheck size={12} /> Audit logged</span>
        </div>
      </div>
    </div>
  );
}
